// Moteur bayésien de classification morphologique.
// Idée : on maintient P(morphologie | réponses). À chaque réponse,
// on met à jour avec la vraisemblance P(réponse | morphologie),
// puis on renormalise. La prochaine question est choisie par
// gain d'information espéré (entropie réduction).

import {
  MORPHOLOGIES,
  type EngineState,
  type LikelihoodVector,
  type Morphology,
  type OptionId,
  type Question,
  type QuestionId,
  type QuestionOption,
} from './types';

const EPSILON = 1e-9;

/** Configuration de l'engine. */
export interface EngineConfig {
  /** Seuil de confiance pour considérer une morphologie comme identifiée (def: 0.85). */
  confidenceThreshold: number;
  /** Nombre maximum de questions de classification (def: 12). */
  maxClassificationQuestions: number;
  /** Plafond absolu de questions (incluant sizing) (def: 20). */
  maxTotalQuestions: number;
  /** Prior uniforme par défaut, peut être surchargé (ex: stats clientes). */
  prior?: LikelihoodVector;
}

export const DEFAULT_CONFIG: EngineConfig = {
  confidenceThreshold: 0.85,
  maxClassificationQuestions: 12,
  maxTotalQuestions: 20,
};

/** Crée un état initial avec un prior uniforme (ou personnalisé). */
export function createInitialState(prior?: LikelihoodVector): EngineState {
  const probs = uniformOrNormalize(prior);
  const top = argmax(probs);
  const zeroPoints: Record<Morphology, number> = { A: 0, V: 0, H: 0, X: 0, '8': 0, O: 0 };
  return {
    probabilities: probs,
    morphoPoints: zeroPoints,
    answers: {},
    asked: [],
    top: { morphology: top, confidence: probs[top] },
    done: false,
  };
}

/**
 * Met à jour l'état après une réponse.
 *   - Si l'option a `points` (système v3 PPT) : additif, puis normalize positif.
 *   - Sinon, fallback Bayes : P(m | r) ∝ P(r | m) × P(m).
 */
export function applyAnswer(
  state: EngineState,
  question: Question,
  optionId: OptionId,
  config: EngineConfig = DEFAULT_CONFIG,
): EngineState {
  const option = question.options.find((o) => o.id === optionId);
  if (!option) {
    throw new Error(`Option inconnue "${optionId}" pour la question "${question.id}"`);
  }

  const next: Record<Morphology, number> = { A: 0, V: 0, H: 0, X: 0, '8': 0, O: 0 };
  const nextPoints: Record<Morphology, number> = { ...state.morphoPoints };

  if (option.points) {
    // Système additif (v3 du PPT)
    for (const m of MORPHOLOGIES) {
      nextPoints[m] += option.points[m] ?? 0;
    }
    // Probabilités = normalize(max(0, points)). Si tous ≤ 0, fallback uniforme.
    let sumPos = 0;
    for (const m of MORPHOLOGIES) {
      const p = Math.max(0, nextPoints[m]);
      next[m] = p;
      sumPos += p;
    }
    if (sumPos > 0) {
      for (const m of MORPHOLOGIES) next[m] /= sumPos;
    } else {
      for (const m of MORPHOLOGIES) next[m] = 1 / MORPHOLOGIES.length;
    }
  } else {
    // Système Bayes (legacy)
    for (const m of MORPHOLOGIES) {
      const prior = state.probabilities[m];
      const lik = Math.max(option.likelihood[m], EPSILON);
      next[m] = prior * lik;
    }
    normalize(next);
  }

  const answers = { ...state.answers, [question.id]: optionId };
  const asked = state.asked.includes(question.id) ? state.asked : [...state.asked, question.id];
  const top = argmax(next);
  const confidence = next[top];

  const enoughConfidence = confidence >= config.confidenceThreshold;
  const tooManyQuestions = asked.length >= config.maxTotalQuestions;
  const enoughClassification =
    asked.length >= config.maxClassificationQuestions && question.kind === 'morphology';

  return {
    ...state,
    probabilities: next,
    morphoPoints: nextPoints,
    answers,
    asked,
    top: { morphology: top, confidence },
    done: enoughConfidence || tooManyQuestions || enoughClassification,
  };
}

/**
 * Remplace la distribution de probabilités (ex: issue des mesures cm)
 * en conservant `asked` et `answers`. Recalcule le top et le statut `done`.
 */
export function withDistribution(
  state: EngineState,
  dist: Record<Morphology, number>,
  config: EngineConfig = DEFAULT_CONFIG,
): EngineState {
  const probs = { ...dist };
  normalize(probs);
  const top = argmax(probs);
  const confidence = probs[top];
  return {
    ...state,
    probabilities: probs,
    top: { morphology: top, confidence },
    done:
      confidence >= config.confidenceThreshold ||
      state.asked.length >= config.maxTotalQuestions,
  };
}

/**
 * Marque une question comme posée sans modifier les probabilités.
 * Utilisé quand l'utilisatrice répond "je ne sais pas" ou clique "Passer".
 * La question ne sera pas reproposée.
 */
export function skipQuestion(
  state: EngineState,
  question: Question,
  config: EngineConfig = DEFAULT_CONFIG,
): EngineState {
  const asked = state.asked.includes(question.id) ? state.asked : [...state.asked, question.id];
  const tooManyQuestions = asked.length >= config.maxTotalQuestions;
  return {
    ...state,
    asked,
    done: state.done || tooManyQuestions,
  };
}

/**
 * Sélection adaptative de la prochaine question : maximise le gain d'information espéré
 * (réduction d'entropie sur la distribution morphologique).
 *
 * EIG(q) = H(P) - Σ_o P(o) × H(P | o)
 * où P(o) = Σ_m P(m) × P(o | m).
 *
 * Retourne `null` si aucune question éligible.
 */
export function selectNextQuestion(
  state: EngineState,
  bank: Question[],
  config: EngineConfig = DEFAULT_CONFIG,
): Question | null {
  const ctx = {
    answers: state.answers,
    topMorphology: state.top.morphology,
    confidence: state.top.confidence,
    probabilities: state.probabilities,
    mode: 'home' as const, // selectNextQuestion non utilisé pour le form (qui passe son propre ctx)
  };

  const candidates = bank.filter((q) => {
    if (state.asked.includes(q.id)) return false;
    if (q.showIf && !q.showIf(ctx)) return false;
    return true;
  });

  if (candidates.length === 0) return null;

  // Toujours servir les questions mandatory en priorité, dans l'ordre du bank.
  const mandatory = candidates.find((q) => q.mandatory);
  if (mandatory) return mandatory;

  // Sinon, gain d'information espéré sur les questions adaptatives restantes
  // (confirmation visuelle ou morphologie subjective legacy).
  const morpho = candidates.filter((q) => q.kind === 'confirmation' || q.kind === 'morphology');
  if (morpho.length === 0) return candidates[0] ?? null;

  let best: Question | null = null;
  let bestGain = -Infinity;
  const currentH = entropy(state.probabilities);

  for (const q of morpho) {
    const gain = currentH - expectedConditionalEntropy(state.probabilities, q.options);
    if (gain > bestGain) {
      bestGain = gain;
      best = q;
    }
  }
  return best ?? morpho[0] ?? null;
}

// --- Helpers ---------------------------------------------------------------

function uniformOrNormalize(prior?: LikelihoodVector): Record<Morphology, number> {
  if (!prior) {
    const n = MORPHOLOGIES.length;
    const out = {} as Record<Morphology, number>;
    for (const m of MORPHOLOGIES) out[m] = 1 / n;
    return out;
  }
  const out = { ...prior } as Record<Morphology, number>;
  normalize(out);
  return out;
}

function normalize(p: Record<Morphology, number>): void {
  let sum = 0;
  for (const m of MORPHOLOGIES) sum += p[m];
  if (sum <= 0) {
    for (const m of MORPHOLOGIES) p[m] = 1 / MORPHOLOGIES.length;
    return;
  }
  for (const m of MORPHOLOGIES) p[m] /= sum;
}

function argmax(p: Record<Morphology, number>): Morphology {
  let best: Morphology = 'A';
  let bestVal = -Infinity;
  for (const m of MORPHOLOGIES) {
    if (p[m] > bestVal) {
      bestVal = p[m];
      best = m;
    }
  }
  return best;
}

function entropy(p: Record<Morphology, number>): number {
  let h = 0;
  for (const m of MORPHOLOGIES) {
    const v = p[m];
    if (v > EPSILON) h -= v * Math.log2(v);
  }
  return h;
}

/**
 * E[H(P | option)] = Σ_o P(o) × H(P | o)
 * où P(o) = Σ_m P(m) × likelihood(o | m) (avant renormalisation par option),
 * puis P(m | o) ∝ P(m) × likelihood(o | m).
 */
function expectedConditionalEntropy(
  prior: Record<Morphology, number>,
  options: QuestionOption[],
): number {
  // Probabilité marginale de chaque option (avant normalisation des likelihoods).
  // On normalise les vraisemblances pour qu'elles forment une distribution sur les options.
  // Pour chaque morphologie m : Σ_o lik(o|m) doit être normalisé à 1.
  const likNorm: number[][] = options.map((o) =>
    MORPHOLOGIES.map((m) => Math.max(o.likelihood[m], EPSILON)),
  );
  // Normaliser par morphologie (colonnes).
  for (let mi = 0; mi < MORPHOLOGIES.length; mi++) {
    let s = 0;
    for (let oi = 0; oi < options.length; oi++) s += likNorm[oi]![mi]!;
    if (s <= 0) continue;
    for (let oi = 0; oi < options.length; oi++) likNorm[oi]![mi]! /= s;
  }

  let expectedH = 0;
  for (let oi = 0; oi < options.length; oi++) {
    // P(option o) = Σ_m P(m) × P(o|m)
    let pO = 0;
    const posterior = {} as Record<Morphology, number>;
    MORPHOLOGIES.forEach((m, mi) => {
      const v = prior[m] * likNorm[oi]![mi]!;
      posterior[m] = v;
      pO += v;
    });
    if (pO <= EPSILON) continue;
    // Normaliser le posterior.
    for (const m of MORPHOLOGIES) posterior[m] /= pO;
    expectedH += pO * entropy(posterior);
  }
  return expectedH;
}
